/**
 * bins.ts — Which bin, out of the several a town has.
 *
 * A level used to have exactly one litter bin, so "the bin" was a point and
 * every question about it was a subtraction. It now has a handful (see
 * `levelgen/landmarks.ts` → `placeBins`), because one bin per town turned the
 * back half of every walk into a search for a single object — and that turned
 * one subtraction into two questions: *am I at one?*, which is what ends the
 * errand, and *which one is nearest?*, which is where the "binned it" burst
 * goes when a town has four of them.
 *
 * Small enough to be a couple of lines in either caller, which is exactly why it
 * is here instead: two copies of "am I at a bin" is how the rule that ends the
 * errand and the pop of confetti that celebrates it drift apart.
 */
import type { Vec2 } from '../utils/vec2';

// `Vec2` is structural — a levelgen `Point` and a live entity position both fit.

/**
 * The bin nearest a point — where a walker with a bag in hand is headed.
 *
 * Returns `null` only for a level with no bins at all, which the validator
 * refuses to ship; callers get to decide what to draw in the meantime rather
 * than being handed a bin that isn't there.
 */
export function nearestBin<T extends Vec2>(from: Vec2, bins: readonly T[]): T | null {
  let best: T | null = null;
  let bestDistSq = Infinity;
  for (const bin of bins) {
    const distSq = (bin.x - from.x) ** 2 + (bin.y - from.y) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = bin;
    }
  }
  return best;
}

/**
 * Is the walker standing at a bin — **any** bin?
 *
 * The rule that ends the errand. Radius rather than a rect because the drop
 * zone is a ring drawn on the ground (`OBJECTIVE.BIN_RADIUS`), and you walk up
 * to a bin from whichever side you happen to arrive on.
 */
export function atAnyBin(from: Vec2, bins: readonly Vec2[], radius: number): boolean {
  return bins.some((bin) => (bin.x - from.x) ** 2 + (bin.y - from.y) ** 2 <= radius ** 2);
}
